const React = require('react');
const { default: MuiAutocomplete } = require('@mui/material/Autocomplete');
const { default: TextField } = require('@mui/material/TextField');
const { default: Styled, css } = require('styled-components');
const { singleLineFieldStyles } = require('./singleLineFieldStyles');
const DropdownOptionStyles = require('./dropdownOptionStyles');
const ClearFieldAdornment = require('./ClearFieldAdornment');
const FieldResolver = require('./useResolvedFieldController');
const InvalidSubmitFocusUtils = require('hooks/useFormPrimitives/invalidSubmitFocusUtils');

const {
    useEffect,
    useMemo,
    useRef,
    useState
} = React;

const internals = {};

internals.DEFAULT_THROTTLE_MS = 250;
internals.EXACT_OPTION_ERROR = 'Must choose an option from the list';

internals.getOptionValue = (option) => option?.id ?? option?.value ?? option ?? null;
internals.getOptionLabel = (option) => option?.name || option?.label || String(option || '');
internals.getSelectedValue = (value) => value;
internals.getExactOptionErrorMessage = (field = {}) => field.autocompleteErrorText || field.exactOptionMessage || internals.EXACT_OPTION_ERROR;

internals.getSyncOptions = ({
    field = {},
    formProps = {},
    options
}) => {

    if (Array.isArray(options)) {
        return options;
    }

    if (Array.isArray(field.options)) {
        return field.options;
    }

    if (typeof field.optionsSource === 'string' && Array.isArray(formProps[field.optionsSource])) {
        return formProps[field.optionsSource];
    }

    return [];
};

internals.filterOptions = ({
    getOptionLabel,
    options = [],
    query
}) => {

    const normalizedQuery = String(query || '').trim().toLowerCase();

    if (!normalizedQuery) {
        return options;
    }

    return options.filter((option) => getOptionLabel(option).toLowerCase().includes(normalizedQuery));
};

internals.mergeOptions = ({
    getOptionValue,
    optionGroups = []
}) => {

    const seen = new Set();

    return optionGroups.flatMap((group) => group || []).filter((option) => {
        const value = getOptionValue(option);
        const key = value === null || typeof value === 'undefined'
            ? internals.getOptionLabel(option)
            : value;

        if (seen.has(key)) {
            return false;
        }

        seen.add(key);
        return true;
    });
};

internals.resolveDataSource = ({
    dataSource,
    field
}) => dataSource || field.dataSource || null;

internals.normalizeOptions = (result) => {

    if (Array.isArray(result)) {
        return result;
    }

    if (Array.isArray(result?.options)) {
        return result.options;
    }

    return [];
};

internals.resolveAutocompleteTextFieldProps = ({
    fieldController,
    textFieldController,
    textFieldName,
    textFieldProps = {}
}) => {

    const fieldTextFieldProps = fieldController.getTextFieldProps?.(textFieldProps) || textFieldProps;

    if (!textFieldController || textFieldName === fieldController.definition?.name) {
        return fieldTextFieldProps;
    }

    const textControllerTextFieldProps = textFieldController.getTextFieldProps?.(textFieldProps) || {};
    const hasTextFieldError = !!textControllerTextFieldProps.error;

    return {
        ...fieldTextFieldProps,
        ...textControllerTextFieldProps,
        fieldStyles: textControllerTextFieldProps.fieldStyles || fieldTextFieldProps.fieldStyles,
        InputProps: {
            ...(fieldTextFieldProps.InputProps || {}),
            ...(textControllerTextFieldProps.InputProps || {})
        },
        inputProps: {
            ...(fieldTextFieldProps.inputProps || {}),
            ...(textControllerTextFieldProps.inputProps || {})
        },
        error: hasTextFieldError || !!fieldTextFieldProps.error,
        helperText: hasTextFieldError
            ? textControllerTextFieldProps.helperText
            : (textControllerTextFieldProps.helperText || fieldTextFieldProps.helperText)
    };
};

internals.notifyFocus = ({
    fieldController,
    textFieldController,
    textFieldName,
    fieldName,
    event
}) => {

    fieldController.onFocus?.(event);

    if (textFieldName !== fieldName) {
        textFieldController.onFocus?.(event);
    }
};

internals.notifyBlur = ({
    fieldController,
    textFieldController,
    textFieldName,
    fieldName,
    event
}) => {

    fieldController.onBlur?.(event);

    if (textFieldName !== fieldName) {
        textFieldController.onBlur?.(event);
    }
};

internals.getErrorFieldName = ({
    field = {},
    fieldName,
    textFieldName
}) => field.errorFieldName || textFieldName || fieldName;

internals.getAutocompleteErrorFieldNames = ({
    fieldName,
    textFieldName
}) => [
    fieldName,
    textFieldName
].filter(Boolean).filter((name, index, names) => names.indexOf(name) === index);

internals.shouldValidateExactOption = (field = {}) => field.exactOptionRequired !== false;

internals.shouldQueryDataSource = ({
    hasActiveQuery,
    hasMatchingSelection
}) => hasActiveQuery && !hasMatchingSelection;

internals.shouldOpenSuggestions = ({
    isLoading,
    shouldQueryDataSource,
    suggestionOptions = []
}) => shouldQueryDataSource && (isLoading || suggestionOptions.length > 0);

module.exports = function AutocompleteField(props) {
    const {
        Autocomplete,
        AutocompleteTextField
    } = internals;

    const {
        clearValue = null,
        dataSource,
        field = {},
        fieldName = field.name,
        fieldValueFromOption: fieldValueFromOptionProp,
        getOptionLabel = internals.getOptionLabel,
        getOptionValue = internals.getOptionValue,
        getSelectedValue = internals.getSelectedValue,
        label = field.label,
        methods = {},
        onBlur,
        onFocus,
        options: propsOptions,
        suppressEmptyState: suppressEmptyStateProp,
        textFieldName = field.textFieldName || `${fieldName}Text`,
        textFieldProps = {},
        throttleMs = field.throttleMs ?? internals.DEFAULT_THROTTLE_MS,
        useField: requestedUseFieldHook
    } = props;
    const suppressEmptyState = typeof suppressEmptyStateProp === 'boolean'
        ? suppressEmptyStateProp
        : !!field.suppressEmptyState;
    const fieldValueFromOption = typeof fieldValueFromOptionProp === 'function'
        ? fieldValueFromOptionProp
        : getOptionValue;

    const requestRef = useRef(0);
    const fieldController = FieldResolver.useResolvedField({
        field,
        fieldName,
        methods,
        requestedUseFieldHook
    });
    const textFieldController = FieldResolver.useResolvedField({
        field: {
            ...field,
            name: textFieldName,
            label
        },
        fieldName: textFieldName,
        methods,
        requestedUseFieldHook
    });
    const formProps = fieldController.formProps || {};
    const selectedValue = fieldController.getValue();
    const selectedComparisonValue = getSelectedValue(selectedValue);
    const selectedLabel = getOptionLabel(selectedValue);
    const textValue = textFieldController.getValue() || '';
    const syncOptions = useMemo(() => internals.getSyncOptions({
        field,
        formProps,
        options: propsOptions
    }), [field, formProps, propsOptions]);
    const [options, setOptions] = useState(syncOptions);
    const [inputValue, setInputValue] = useState(textValue);
    const [isLoading, setIsLoading] = useState(false);
    const resolvedTextFieldProps = internals.resolveAutocompleteTextFieldProps({
        fieldController,
        textFieldController,
        textFieldName,
        textFieldProps
    });
    const hasError = !!resolvedTextFieldProps.error;
    const helperText = resolvedTextFieldProps.helperText;
    const resolvedDataSource = internals.resolveDataSource({
        dataSource,
        field
    });
    const normalizedInputValue = String(inputValue || '').trim();
    const hasActiveQuery = normalizedInputValue.length > 0;
    const hasMatchingSelection = !!selectedComparisonValue && normalizedInputValue === String(selectedLabel || '').trim();
    const shouldQueryDataSource = internals.shouldQueryDataSource({
        hasActiveQuery,
        hasMatchingSelection
    });

    useEffect(() => {
        setInputValue(textValue);
    }, [textValue]);

    useEffect(() => {
        const requestId = requestRef.current + 1;
        requestRef.current = requestId;

        const timer = setTimeout(() => {

            if (typeof resolvedDataSource === 'function') {
                if (!shouldQueryDataSource) {
                    setIsLoading(false);
                    setOptions([]);
                    return;
                }

                setIsLoading(true);

                Promise.resolve(resolvedDataSource({
                    field,
                    formProps,
                    query: inputValue
                })).then((nextOptions) => {

                    if (requestRef.current !== requestId) {
                        return;
                    }

                    setIsLoading(false);
                    setOptions(internals.normalizeOptions(nextOptions));
                }).catch(() => {

                    if (requestRef.current === requestId) {
                        setIsLoading(false);
                        setOptions([]);
                    }
                });
                return;
            }

            setIsLoading(false);
            setOptions(internals.filterOptions({
                getOptionLabel,
                options: syncOptions,
                query: inputValue
            }));
        }, throttleMs);

        return () => {
            clearTimeout(timer);
        };
    }, [field, formProps, getOptionLabel, inputValue, resolvedDataSource, shouldQueryDataSource, syncOptions, throttleMs]);

    const suggestionOptions = useMemo(() => internals.mergeOptions({
        getOptionValue: (option) => internals.getComparableValue({
            getOptionValue,
            getSelectedValue,
            value: option
        }),
        optionGroups: [options, syncOptions]
    }), [getOptionValue, getSelectedValue, options, syncOptions]);

    const mergedOptions = useMemo(() => internals.mergeOptions({
        getOptionValue: (option) => internals.getComparableValue({
            getOptionValue,
            getSelectedValue,
            value: option
        }),
        optionGroups: [
            suggestionOptions,
            internals.hasDisplayValue({
                getOptionLabel,
                getOptionValue,
                getSelectedValue,
                value: selectedValue
            }) ? [selectedValue] : []
        ]
    }), [getOptionLabel, getOptionValue, getSelectedValue, selectedValue, suggestionOptions]);

    const selectedOption = useMemo(() => mergedOptions.find((option) => internals.getComparableValue({
        getOptionValue,
        getSelectedValue,
        value: option
    }) === selectedComparisonValue) || null, [
        getOptionValue,
        getSelectedValue,
        mergedOptions,
        selectedComparisonValue
    ]);
    const selectedOptionLabel = selectedOption ? getOptionLabel(selectedOption) : '';
    const inputMatchesSelectedOption = !!selectedOption && normalizedInputValue === String(selectedOptionLabel || '').trim();
    const shouldOpen = internals.shouldOpenSuggestions({
        isLoading,
        shouldQueryDataSource,
        suggestionOptions
    });
    const errorFieldName = internals.getErrorFieldName({
        field,
        fieldName,
        textFieldName
    });
    const errorFieldNames = internals.getAutocompleteErrorFieldNames({
        fieldName,
        textFieldName
    });

    const clearAutocompleteErrors = () => {

        if (errorFieldNames.length === 1) {
            methods.clearErrors?.(errorFieldNames[0]);
            return;
        }

        methods.clearErrors?.(errorFieldNames);
    };

    const setExactOptionError = () => {

        methods.setError?.(errorFieldName, {
            type: 'manual',
            message: internals.getExactOptionErrorMessage(field)
        });
    };

    const validateExactOptionSelection = () => {

        if (!internals.shouldValidateExactOption(field)) {
            return;
        }

        if (!normalizedInputValue || inputMatchesSelectedOption) {
            clearAutocompleteErrors();
            return;
        }

        setExactOptionError();
    };

    const clearSelection = (event) => {

        setInputValue('');
        textFieldController.setValue('', undefined, event);
        fieldController.setValue(clearValue, undefined, event);
        setOptions([]);
        clearAutocompleteErrors();
    };

    const updateTextValue = (nextInputValue, event) => {
        const normalizedValue = nextInputValue || '';

        setInputValue(normalizedValue);
        textFieldController.setValue(normalizedValue, undefined, event);
        clearAutocompleteErrors();

        if (!normalizedValue) {
            fieldController.setValue(clearValue, undefined, event);
            return;
        }

        if (selectedOption && normalizedValue === getOptionLabel(selectedOption)) {
            return;
        }

        fieldController.setValue(clearValue, undefined, event);
    };

    return <Autocomplete
        options={mergedOptions}
        open={suppressEmptyState ? shouldOpen : undefined}
        value={selectedOption}
        inputValue={inputValue}
        loading={isLoading}
        forcePopupIcon={false}
        disableClearable
        loadingText={null}
        noOptionsText={suppressEmptyState ? '' : undefined}
        filterOptions={(nextOptions) => nextOptions}
        onInputChange={(event, nextInputValue, reason) => {

            if (reason === 'reset') {
                return;
            }

            updateTextValue(nextInputValue, event);
        }}
        onChange={(event, nextOption) => {

            if (!nextOption) {
                clearSelection(event);
                return;
            }

            const nextLabel = getOptionLabel(nextOption);

            setInputValue(nextLabel);
            textFieldController.setValue(nextLabel, undefined, event);
            fieldController.setValue(fieldValueFromOption(nextOption), undefined, event);
            clearAutocompleteErrors();
        }}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) => internals.getComparableValue({
            getOptionValue,
            getSelectedValue,
            value: option
        }) === internals.getComparableValue({
            getOptionValue,
            getSelectedValue,
            value
        })}
        renderInput={(params) => <AutocompleteTextField
            {...params}
            {...resolvedTextFieldProps}
            fullWidth
            $fieldStyles={resolvedTextFieldProps.fieldStyles}
            label={label || fieldName}
            placeholder=''
            error={hasError}
            helperText={helperText}
            onBlur={(event) => {
                internals.notifyBlur({
                    event,
                    fieldController,
                    fieldName,
                    textFieldController,
                    textFieldName
                });
                onBlur?.(event);
                validateExactOptionSelection();
            }}
            onFocus={(event) => {
                if (!InvalidSubmitFocusUtils.shouldPreserveErrorOnFocus(event)) {
                    clearAutocompleteErrors();
                }
                internals.notifyFocus({
                    event,
                    fieldController,
                    fieldName,
                    textFieldController,
                    textFieldName
                });
                onFocus?.(event);
            }}
            InputProps={{
                ...params.InputProps,
                endAdornment: <ClearFieldAdornment
                    clearLabel={`Clear ${label || fieldName}`}
                    disabled={!!resolvedTextFieldProps.disabled || !inputValue}
                    onClear={clearSelection}
                    existingEndAdornment={params.InputProps.endAdornment}
                />
            }}
            inputRef={resolvedTextFieldProps.inputRef}
            inputProps={{
                ...params.inputProps,
                ...(resolvedTextFieldProps.inputProps || {}),
                'aria-invalid': hasError || undefined
            }}
        />}
    />;
};

internals.getComparableValue = ({
    getOptionValue,
    getSelectedValue,
    value
}) => {

    if (!value) {
        return value;
    }

    const optionValue = getOptionValue(value);

    if (optionValue !== null && typeof optionValue !== 'undefined' && optionValue !== '') {
        return optionValue;
    }

    return getSelectedValue(value);
};

internals.hasDisplayValue = ({
    getOptionLabel,
    getOptionValue,
    getSelectedValue,
    value
}) => {

    const comparableValue = internals.getComparableValue({
        getOptionValue,
        getSelectedValue,
        value
    });

    if (comparableValue !== null && typeof comparableValue !== 'undefined' && comparableValue !== '') {
        return true;
    }

    return String(getOptionLabel(value) || '').trim().length > 0;
};

internals.AutocompleteTextField = Styled(TextField)`
    ${singleLineFieldStyles}

    & .MuiInputBase-root {
        align-items: center;
        flex-wrap: nowrap !important;
        padding-right: 0 !important;
    }

    & .MuiAutocomplete-input {
        min-width: 0 !important;
    }

    ${({ $fieldStyles }) => css`${$fieldStyles || ''}`}
`;

internals.Autocomplete = Styled(MuiAutocomplete)`
    & .MuiAutocomplete-option {
        ${DropdownOptionStyles.option}
    }
`;

module.exports.internals = internals;
